import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Segmented, Spin, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  BankOutlined,
  CalendarOutlined,
  CloseOutlined,
  FallOutlined,
  FundOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  PieChartOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { authService } from '../../../services/auth';
import { formatCurrency as plainCurrency } from '../../../utils/currency';
import { ReportFigure, ReportLongTable, ReportSection } from '../ReportTab';
import { Donut, money as plainMoney, shortMoney } from '../managementDashboard/DashboardCharts';
import { fetchCashBalances, type LiquidityBalance, type LiquidityItem, type LiquidityReport } from '../managementDashboard/liquidityApi';
import { monthShortLabel } from '../incomeStatement/incomeStatement';
import CashBalanceModal from './CashBalanceModal';
import {
  itemHref,
  itemsTotal,
  type InvoiceAccess,
  liquidityBars,
  liquidityRows,
  periodLabel,
  type LiquidityMode,
  type LiquidityRow,
} from './liquidityView';
import './LiquiditySection.css';

// „Płynność” tab of „Wynik firmy” (decision 2026-10-29-plynnosc-saldo-13-tygodni):
// the typed cash balance, 13 weeks / 12 months of balances from payment due
// dates and fixed costs, a period opens its documents. Money comes as it is
// from GET /reports/liquidity; this view names, orders and draws it.

interface Props {
  report: LiquidityReport | null;
  failed: boolean;
  /** Reload the report after a typed balance. */
  onSaved: () => void;
}

const formatDay = (value: string | null | undefined) => (value ? dayjs(value).format('DD.MM.YYYY') : '—');
const shortDay = (value: string) => dayjs(value).format('DD.MM');
// Negative amounts with the typographic minus, like the notes (review of PR5).
const minus = (text: string) => text.replace(/^-/, '−');
const money = (value: number | null | undefined) => minus(plainMoney(value));
const formatCurrency = (value: number, currency: string) => minus(plainCurrency(value, currency));
const signed = (value: number) => (value > 0 ? `+ ${money(value)}` : value < 0 ? `− ${money(Math.abs(value))}` : money(0));

const LiquiditySection: React.FC<Props> = ({ report, failed, onSaved }) => {
  const { t, i18n } = useTranslation(['reports', 'fixedCosts', 'common']);
  const language = i18n.language || 'pl';
  const k = useCallback((key: string, options?: Record<string, unknown>) => t(`reports:liquidity.${key}`, options ?? {}) as string, [t]);
  const canEnter = authService.canEnterCashBalance();
  const invoiceAccess: InvoiceAccess = {
    sales: authService.canManageInvoiceImports(),
    purchase: authService.canManagePurchaseInvoiceImports(),
  };
  const [mode, setMode] = useState<LiquidityMode>('weeks');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [history, setHistory] = useState<LiquidityBalance[] | null>(null);
  const [historyFailed, setHistoryFailed] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setHistoryFailed(false);
    fetchCashBalances(6)
      .then((rows) => { if (!cancelled) setHistory(rows); })
      .catch(() => { if (!cancelled) setHistoryFailed(true); });
    return () => { cancelled = true; };
  }, [historyVersion]);

  const rows = useMemo(
    () => (report ? liquidityRows(report.forecast, mode, report.balance?.amount ?? null) : []),
    [report, mode],
  );
  const periods = useMemo(() => (report ? (mode === 'weeks' ? report.forecast.weeks : report.forecast.months) : []), [report, mode]);
  const bars = useMemo(() => liquidityBars(periods), [periods]);
  const selected = rows.find((row) => row.key === selectedKey) ?? null;

  // Another mode has other rows: close the list then.
  useEffect(() => { setSelectedKey(null); }, [mode]);

  const saved = () => {
    onSaved();
    setHistoryVersion((version) => version + 1);
  };

  if (failed) return <Alert type="error" showIcon message={t('reports:managementDashboard.errors.liquidity')} />;
  if (!report) return <div className="liquidity__loading"><Spin /></div>;

  const { balance, forecast } = report;
  const hasBalance = balance !== null && forecast.openingBalance !== null;
  const weeksLast = forecast.weeks[forecast.weeks.length - 1];
  const monthsLast = forecast.months[forecast.months.length - 1];
  const enterButton = (primary: boolean, testId: string) => (canEnter ? (
    <Button size="small" type={primary ? 'primary' : 'default'} icon={<BankOutlined />} onClick={() => setModalOpen(true)} data-testid={testId}>
      {k('enterBalance')}
    </Button>
  ) : null);

  const rowLabel = (row: LiquidityRow, index: number) => {
    if (row.kind === 'overdue') return k('rowOverdue');
    if (row.kind === 'undated') return k('rowUndated', { count: row.items.length });
    if (row.kind === 'bridge') return k('rowBridge', { from: shortDay(row.start), to: shortDay(row.end as string) });
    const periodIndex = index - rows.filter((other) => other.kind !== 'period').length;
    return periodLabel(row, mode, periodIndex, language);
  };
  const balanceCell = (value: number | null, testId?: string) => (
    value === null
      ? <span className="report-muted">—</span>
      : <span className={`report-num${value < 0 ? ' report-negative' : ''}`} title={formatCurrency(value, 'PLN')} data-testid={testId}>{money(value)}</span>
  );
  const flowCell = (value: number) => (
    <span className={`report-num${value === 0 ? ' report-muted' : ''}`} title={formatCurrency(value, 'PLN')}>{money(value)}</span>
  );

  const columns: ColumnsType<LiquidityRow> = [
    {
      title: mode === 'weeks' ? k('colWeek') : k('colMonth'),
      key: 'period',
      render: (_, row, index) => {
        const label = rowLabel(row, index);
        return <span className={`report-cut${row.kind === 'period' ? '' : ' report-muted'}`} title={row.kind === 'overdue' ? k('rowOverdueHint') : row.kind === 'undated' ? k('rowUndatedHint') : label}>{label}</span>;
      },
    },
    { title: k('colOpening'), key: 'opening', align: 'right', width: 132, render: (_, row) => balanceCell(row.openingBalance) },
    { title: k('colInflows'), key: 'inflows', align: 'right', width: 112, render: (_, row) => flowCell(row.inflows) },
    { title: k('colOutflows'), key: 'outflows', align: 'right', width: 112, render: (_, row) => flowCell(row.outflows) },
    {
      title: <span title={k('colFixedHint', { day: forecast.fixedCostPaymentDay })}>{k('colFixed')}</span>,
      key: 'fixed',
      align: 'right',
      width: 156,
      className: 'liquidity__col-fixed',
      render: (_, row) => (row.kind === 'undated' ? <span className="report-muted">—</span> : flowCell(row.fixedCosts)),
    },
    { title: k('colClosing'), key: 'closing', align: 'right', width: 140, render: (_, row) => balanceCell(row.closingBalance, 'liquidity-row-closing') },
  ];

  const itemTitle = (item: LiquidityItem) => (item.kind === 'FIXED_COST'
    ? k('item.FIXED_COST', { name: item.name || '—' })
    : k(`item.${item.kind}`, { number: item.documentNumber || '—' }));
  const itemParty = (item: LiquidityItem) => {
    if (item.kind !== 'FIXED_COST') return item.counterparty || '—';
    const category = item.category ? t(`fixedCosts:categories.${item.category}`, { defaultValue: item.category }) as string : '—';
    if (item.paymentDateUnknown) return `${category} · ${k('paymentDateUnknown')}`;
    return item.projected ? `${category} · ${k('projected')}` : category;
  };
  const itemColumns: ColumnsType<LiquidityItem> = [
    {
      title: k('colDocument'),
      key: 'document',
      render: (_, item) => {
        const href = itemHref(item, invoiceAccess);
        return (
        <span className="report-cut" title={itemTitle(item)}>
          {href ? <Link to={href}>{itemTitle(item)}</Link> : itemTitle(item)}
          {item.factoring && <Tag className="liquidity__tag">{k('factoring')}</Tag>}
        </span>
        );
      },
    },
    { title: k('colParty'), key: 'party', render: (_, item) => <span className="report-cut" title={itemParty(item)}>{itemParty(item)}</span> },
    {
      title: selected?.kind === 'overdue' ? k('colDue') : selected?.kind === 'undated' ? k('colIssued') : k('colDate'),
      key: 'date',
      width: 150,
      render: (_, item) => (
        <span className="report-cut report-num">
          {formatDay(item.date || item.issueDate)}
          {item.daysOverdue !== null && <span className="report-negative">{` · ${k('daysOverdue', { count: item.daysOverdue })}`}</span>}
        </span>
      ),
    },
    { title: k('colInflow'), key: 'in', align: 'right', width: 132, render: (_, item) => (item.flow === 'IN' ? flowCell(item.amount) : null) },
    { title: k('colOutflow'), key: 'out', align: 'right', width: 132, render: (_, item) => (item.flow === 'OUT' ? flowCell(item.amount) : null) },
  ];

  // Figures: with a balance the balances, without one the net flows and a call to type it.
  const undatedCount = forecast.undated.count;
  const balanceNote = !balance
    ? k('noBalanceNote')
    : balance.stale
      ? k('staleNote', { date: shortDay(balance.balanceDate) })
      : k('balanceNote', { date: formatDay(balance.balanceDate), who: balance.createdByName || '—' });
  const figures = (
    <div className="report-figures liquidity__figures">
      <ReportFigure
        icon={<WalletOutlined />}
        tone="blue"
        testId="liquidity-figure-today"
        label={k('figureToday')}
        value={hasBalance ? money(forecast.openingBalance) : k('noBalance')}
        valueClassName={!hasBalance ? 'is-missing' : (forecast.openingBalance as number) < 0 ? 'is-negative' : undefined}
        note={<span className={balance?.stale || !balance ? 'liquidity__note-warning' : undefined}>{balanceNote}</span>}
        noteTitle={balance ? k('balanceNoteTitle', {
          date: formatDay(balance.balanceDate),
          amount: formatCurrency(balance.amount, 'PLN'),
          who: balance.createdByName || '—',
          note: balance.note || '—',
        }) : k('noBalanceHint')}
      />
      <ReportFigure
        icon={<CalendarOutlined />}
        tone="indigo"
        testId="liquidity-figure-weeks"
        label={hasBalance ? k('figureWeeks') : k('figureWeeksNet')}
        value={hasBalance ? money(weeksLast?.closingBalance) : signed(forecast.weeksNet)}
        valueClassName={(hasBalance ? (weeksLast?.closingBalance ?? 0) : forecast.weeksNet) < 0 ? 'is-negative' : undefined}
        note={undatedCount > 0
          ? <span className="liquidity__note-warning">{k('undatedNote', { count: undatedCount })}</span>
          : hasBalance
            ? k('figureWeeksNote', { date: formatDay(weeksLast?.end), change: signed(forecast.weeksNet) })
            : k('flowsOnlyNote', { date: formatDay(weeksLast?.end) })}
        noteTitle={[
          hasBalance ? k('figureChangeTitle', { date: formatDay(weeksLast?.end), change: signed(forecast.weeksNet) }) : k('flowsOnlyNote', { date: formatDay(weeksLast?.end) }),
          ...(undatedCount > 0 ? [k('undatedNoteTitle', { count: undatedCount, outflows: money(forecast.undated.outflows), inflows: money(forecast.undated.inflows) })] : []),
        ].join('. ')}
      />
      <ReportFigure
        icon={<FallOutlined />}
        tone="orange"
        testId="liquidity-figure-lowest"
        label={k('figureLowest')}
        value={forecast.lowestWeek ? money(forecast.lowestWeek.closingBalance) : k('noBalance')}
        valueClassName={!forecast.lowestWeek ? 'is-missing' : forecast.lowestWeek.closingBalance < 0 ? 'is-negative' : undefined}
        note={forecast.lowestWeek
          ? k('figureLowestNote', { week: `${shortDay(forecast.lowestWeek.start)}–${shortDay(forecast.lowestWeek.end)}` })
          : k('noBalanceNote')}
      />
      <ReportFigure
        icon={<FundOutlined />}
        tone="teal"
        testId="liquidity-figure-months"
        label={hasBalance ? k('figureMonths') : k('figureMonthsNet')}
        value={hasBalance ? money(monthsLast?.closingBalance) : signed(forecast.monthsNet)}
        valueClassName={(hasBalance ? (monthsLast?.closingBalance ?? 0) : forecast.monthsNet) < 0 ? 'is-negative' : undefined}
        note={hasBalance
          ? k('figureMonthsNote', { date: formatDay(monthsLast?.end), change: signed(forecast.monthsNet) })
          : k('flowsOnlyNote', { date: formatDay(monthsLast?.end) })}
        noteTitle={hasBalance ? k('figureChangeTitle', { date: formatDay(monthsLast?.end), change: signed(forecast.monthsNet) }) : undefined}
      />
    </div>
  );

  const negativeCount = periods.filter((period) => period.closingBalance !== null && period.closingBalance < 0).length;
  const chart = (
    <div className="liquidity-bars" data-testid="liquidity-chart" aria-label={bars.showsBalance ? k('chartBalance') : k('chartNet')}>
      <div className="liquidity-bars__plot">
        {bars.bars.map((bar, index) => {
          const period = periods[index];
          const rowKey = `p-${index}`;
          const label = periodLabel(period, mode, index, language);
          const title = `${label}: ${bars.showsBalance ? k('chartBalanceTitle', { amount: money(bar.value) }) : k('chartNetTitle', { amount: signed(bar.value) })}`;
          return (
            <button
              type="button"
              key={bar.key}
              className={`liquidity-bars__col${selectedKey === rowKey ? ' is-selected' : ''}`}
              title={title}
              aria-label={title}
              onClick={() => setSelectedKey(selectedKey === rowKey ? null : rowKey)}
            >
              <span className="liquidity-bars__up" style={{ flexGrow: bars.positiveShare }}>
                {!bar.negative && <i className="liquidity-bars__bar" style={{ height: `${bar.height}%` }} />}
              </span>
              <span className="liquidity-bars__down" style={{ flexGrow: 1 - bars.positiveShare }}>
                {bar.negative && <i className="liquidity-bars__bar is-negative" style={{ height: `${bar.height}%` }} />}
              </span>
              <span className="liquidity-bars__label">{mode === 'weeks' ? shortDay(period.start) : monthShortLabel(period.start.slice(0, 7), language)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const detailTitle = selected
    ? selected.kind === 'overdue'
      ? k('detailOverdue')
      : selected.kind === 'undated'
        ? k('detailUndated')
      : selected.kind === 'bridge'
        ? k('detailBridge', { from: formatDay(selected.start), to: formatDay(selected.end) })
        : k('detailPeriod', { period: `${formatDay(selected.start)}–${formatDay(selected.end)}` })
    : '';

  return (
    <div className="liquidity" data-testid="liquidity">
      <p className="report-muted report-cut liquidity__basis" data-testid="liquidity-basis" title={k('basis', { date: formatDay(report.asOf), day: forecast.fixedCostPaymentDay })}>
        {k('basisLine', { date: formatDay(report.asOf) })}
      </p>
      {figures}

      <ReportSection
        title={k('forecastTitle')}
        icon={<CalendarOutlined />}
        tone="indigo"
        lead={bars.showsBalance
          ? (negativeCount > 0 ? k('leadNegative', { count: negativeCount }) : k('lead'))
          : k('leadNoBalance')}
        className="liquidity__forecast"
        actions={(
          <>
            <Segmented<LiquidityMode>
              size="small"
              value={mode}
              onChange={setMode}
              options={[{ value: 'weeks', label: k('modeWeeks') }, { value: 'months', label: k('modeMonths') }]}
              data-testid="liquidity-mode"
            />
            <Tooltip title={k('basis', { date: formatDay(report.asOf), day: forecast.fixedCostPaymentDay })} placement="bottomRight">
              <InfoCircleOutlined className="report-muted" aria-label={k('howTitle')} />
            </Tooltip>
            {enterButton(!hasBalance || balance?.stale === true, 'liquidity-enter-balance')}
          </>
        )}
      >
        {chart}
        <ReportLongTable rowCount={rows.length} testId="liquidity-rows">
          <div className="report-table liquidity__table" data-testid="liquidity-table">
            <Table<LiquidityRow>
              rowKey="key"
              size="small"
              tableLayout="fixed"
              columns={columns}
              dataSource={rows}
              pagination={false}
              rowClassName={(row) => `liquidity__row is-${row.kind}${row.key === selectedKey ? ' is-selected' : ''}`}
              onRow={(row) => ({
                onClick: () => setSelectedKey(row.key === selectedKey ? null : row.key),
                'data-testid': `liquidity-row-${row.kind}`,
              } as React.HTMLAttributes<HTMLElement>)}
            />
          </div>
        </ReportLongTable>
        {!selected && <p className="report-muted report-cut liquidity__hint" title={k('openHint')}>{k('openHint')}</p>}
      </ReportSection>

      {selected && (
        <ReportSection
          title={detailTitle}
          icon={<CalendarOutlined />}
          tone="indigo"
          count={selected.items.length}
          lead={selected.kind === 'overdue' ? k('detailOverdueLead') : selected.kind === 'undated' ? k('detailUndatedLead') : k('detailLead', { day: forecast.fixedCostPaymentDay })}
          className="liquidity__detail"
          actions={<Button size="small" type="text" icon={<CloseOutlined />} aria-label={k('close')} title={k('close')} onClick={() => setSelectedKey(null)} />}
        >
          <div className="report-table liquidity__table" data-testid="liquidity-detail">
            <Table<LiquidityItem>
              rowKey={(item) => `${item.kind}-${item.id}`}
              size="small"
              tableLayout="fixed"
              columns={itemColumns}
              dataSource={selected.items}
              pagination={false}
              locale={{ emptyText: k('detailEmpty') }}
              onRow={() => ({ 'data-testid': 'liquidity-detail-item' } as React.HTMLAttributes<HTMLElement>)}
              summary={() => (selected.items.length ? (
                <Table.Summary>
                  <Table.Summary.Row className="liquidity__total" data-testid="liquidity-detail-total">
                    <Table.Summary.Cell index={0} colSpan={3}>{k('total', { count: selected.items.length })}</Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">{flowCell(itemsTotal(selected.items, 'IN'))}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">{flowCell(itemsTotal(selected.items, 'OUT'))}</Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null)}
            />
          </div>
        </ReportSection>
      )}

      <div className="liquidity__pair">
        <ReportSection title={k('agingTitle')} icon={<PieChartOutlined />} tone="green" lead={k('agingLead')}>
          <Donut
            language={language}
            center={shortMoney(report.receivables.total, language)}
            centerCaption={k('agingCaption')}
            emptyText={k('noReceivables')}
            items={[
              { key: 'notDue', label: k('notDue'), value: report.receivables.aging.notDue, color: 'var(--mgmt-series-1)' },
              { key: 'd14', label: k('days1to14'), value: report.receivables.aging.days1to14, color: 'var(--mgmt-series-2)' },
              { key: 'd30', label: k('days15to30'), value: report.receivables.aging.days15to30, color: 'var(--mgmt-series-3)' },
              { key: 'over', label: k('over30'), value: report.receivables.aging.over30, color: 'var(--mgmt-series-5)' },
            ]}
          />
        </ReportSection>
        <ReportSection
          title={k('historyTitle')}
          icon={<HistoryOutlined />}
          tone="gray"
          lead={k('historyLead')}
          actions={enterButton(false, 'liquidity-enter-balance-history')}
        >
          {historyFailed && <span className="report-muted">{k('historyFailed')}</span>}
          {!historyFailed && history === null && <Spin size="small" />}
          {!historyFailed && history !== null && history.length === 0 && <span className="report-muted">{k('historyEmpty')}</span>}
          {!historyFailed && history !== null && history.length > 0 && (
            <dl className="obj-facts liquidity__history" data-testid="liquidity-history">
              {history.map((entry) => {
                const who = `${entry.createdByName || '—'} · ${entry.createdAt ? dayjs(entry.createdAt).format('DD.MM.YYYY HH:mm') : '—'}${entry.note ? ` · ${entry.note}` : ''}`;
                return (
                  <div className="obj-facts__row" key={entry.id}>
                    <dt title={who}>
                      <span className="liquidity__history-date">{formatDay(entry.balanceDate)}</span>
                      <span className="report-muted">{who}</span>
                    </dt>
                    <dd className={`report-num${entry.amount < 0 ? ' report-negative' : ''}`}>{money(entry.amount)}</dd>
                  </div>
                );
              })}
            </dl>
          )}
        </ReportSection>
      </div>

      <CashBalanceModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={saved} />
    </div>
  );
};

export default LiquiditySection;
